import React, { useState, useCallback } from 'react';
import { User, MapPin, CreditCard, FileText, Loader2, ArrowLeft, Truck, ChevronRight } from 'lucide-react';
import { Input, Select } from './ui';
import { CartItem } from './CartDrawer';
import orderLinkAPI, { FreteInfo, ClientAddress, buildFullAddress } from '../services/orderLinkAPI';
import AddressSelectionModal from './AddressSelectionModal';

interface CheckoutFormProps {
  cartItems: CartItem[];
  meiosPagamento?: string;
  linkId: string;
  onSubmit: (data: CheckoutFormData) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export interface CheckoutFormData {
  nome_cliente: string;
  endereco: string;
  meio_pagamento: string;
  troco?: string;
  observacoes?: string;
  retirada_local?: boolean;
  valor_frete?: number;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  cartItems,
  meiosPagamento,
  linkId,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<CheckoutFormData>({
    nome_cliente: '',
    endereco: '',
    meio_pagamento: '',
    troco: '',
    observacoes: '',
    retirada_local: false,
  });

  const [errors, setErrors] = useState<Partial<CheckoutFormData>>({});
  const [freteInfo, setFreteInfo] = useState<Partial<FreteInfo>>({});
  const [calculatingFrete, setCalculatingFrete] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<ClientAddress | null>(null);

  // Helper function to normalize payment method names with emojis
  const normalizePaymentMethod = (method: string): string => {
    const normalized = method.trim().toLowerCase();

    // Mapeamento de nomes para versões formatadas
    const paymentMap: Record<string, string> = {
      'dinheiro': 'Dinheiro',
      'pix': 'PIX',
      'cartao_debito': 'Cartão de Débito',
      'cartao_credito': 'Cartão de Crédito',
      'cartão de débito': 'Cartão de Débito',
      'cartao de debito': 'Cartão de Débito',
      'cartão de crédito': 'Cartão de Crédito',
      'cartao de credito': 'Cartão de Crédito',
      'débito': 'Cartão de Débito',
      'debito': 'Cartão de Débito',
      'crédito': 'Cartão de Crédito',
      'credito': 'Cartão de Crédito',
      'cartão': 'Cartão',
      'cartao': 'Cartão',
      'vr': 'Vale Refeição (VR)',
      'va': 'Vale Alimentação (VA)',
    };

    return paymentMap[normalized] || `${method.trim()}`;
  };

  // Parse meios de pagamento from string
  const paymentMethodsList = meiosPagamento
    ? meiosPagamento.split(',').map(m => m.trim()).filter(m => m)
    : ['Dinheiro', 'PIX', 'Cartão de Débito', 'Cartão de Crédito'];

  const paymentMethods = paymentMethodsList.map(method => ({
    value: method,
    label: normalizePaymentMethod(method)
  }));

  const calculateSubtotal = (): number => {
    return cartItems.reduce((total, item) => {
      const itemPrice = item.finalPrice || item.product.price;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  // Calcular valores do pedido
  const subtotal = calculateSubtotal();
  const valorFrete = formData.retirada_local ? 0 : (freteInfo.valor_frete || 0);
  const total = subtotal + valorFrete;
  const showTrocoField = formData.meio_pagamento.toLowerCase() === 'dinheiro';

  const validateForm = (): boolean => {
    const newErrors: Partial<CheckoutFormData> = {};

    if (!formData.nome_cliente.trim()) {
      newErrors.nome_cliente = 'Nome é obrigatório';
    }

    // Endereço só é obrigatório se não for retirada no local
    if (!formData.retirada_local && !formData.endereco.trim()) {
      newErrors.endereco = 'Endereço é obrigatório';
    }

    if (!formData.meio_pagamento) {
      newErrors.meio_pagamento = 'Selecione uma forma de pagamento';
    }

    if (
      formData.meio_pagamento.toLowerCase() === 'dinheiro' &&
      formData.troco &&
      parseFloat(formData.troco) < total
    ) {
      newErrors.troco = 'Valor do troco deve ser maior que o total';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      // Incluir valor do frete nos dados do pedido
      onSubmit({
        ...formData,
        endereco: formData.retirada_local ? 'RETIRAR NO LOCAL' : formData.endereco,
        valor_frete: valorFrete
      });
    }
  };

  const handleChange = (field: keyof CheckoutFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Função para calcular frete quando endereço muda
  const calculateFreteForAddress = useCallback(async (
    endereco: string,
    structured?: { bairro?: string; cidade?: string; estado?: string }
  ) => {
    if (!endereco.trim() || !linkId) {
      setFreteInfo({});
      return;
    }

    setCalculatingFrete(true);
    setFreteInfo({});

    try {
      const data = await orderLinkAPI.calculateFrete(linkId, endereco, structured);
      setFreteInfo(data);

      if (!data.can_deliver) {
        setErrors(prev => ({
          ...prev,
          endereco: `Endereço fora da área de entrega (máx. ${data.max_distance}km)`
        }));
      }
    } catch (err: any) {
      console.error('Erro ao calcular frete:', err);
      setFreteInfo({});
    } finally {
      setCalculatingFrete(false);
    }
  }, [linkId]);

  // Handler para quando um endereço é selecionado no modal
  const handleAddressSelect = (address: ClientAddress) => {
    setSelectedAddress(address);
    const fullAddress = buildFullAddress(address);
    handleChange('endereco', fullAddress);
    setErrors(prev => ({ ...prev, endereco: undefined }));
    calculateFreteForAddress(fullAddress, {
      bairro: address.bairro,
      cidade: address.cidade,
      estado: address.estado
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-[4px] transition-colors"
              disabled={isSubmitting}
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-[22px] font-extrabold tracking-tight text-gray-900">
              Finalizar Pedido
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Resumo do Pedido */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900 mb-4">
              Resumo do Pedido
            </h2>
            <div className="space-y-2">
              {cartItems.map((item, index) => {
                const itemPrice = item.finalPrice || item.product.price;
                const displayName = (item as any).displayName || item.product.name;
                // Generate unique key based on product + attributes
                const itemKey = JSON.stringify({
                  productId: item.product.id,
                  attributes: item.product_variables || {},
                  index
                });
                return (
                  <div
                    key={itemKey}
                    className="flex justify-between text-[14px]"
                  >
                    <span className="text-gray-700">
                      {item.quantity}x {displayName}
                    </span>
                    <span className="font-semibold text-gray-900">
                      R$ {(itemPrice * item.quantity).toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                );
              })}
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                <div className="flex justify-between text-[14px]">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">
                    R$ {subtotal.toFixed(2).replace('.', ',')}
                  </span>
                </div>
                {valorFrete > 0 && (
                  <div className="flex justify-between text-[14px]">
                    <span className="text-gray-600">Frete</span>
                    <span className="text-gray-900">
                      R$ {valorFrete.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-[18px] pt-1">
                  <span className="text-gray-900">Total</span>
                  <span style={{ color: 'var(--text-color)' }}>
                    R$ {total.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dados Pessoais */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Seus Dados
              </h2>
            </div>

            <div>
              <Input
                label="Nome Completo *"
                type="text"
                value={formData.nome_cliente}
                onChange={(e: any) => handleChange('nome_cliente', e.target.value)}
                placeholder="Digite seu nome completo"
                error={errors.nome_cliente}
              />
            </div>
          </div>

          {/* Endereço */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Endereço de Entrega
              </h2>
            </div>

            {/* Checkbox Retirada no Local */}
            <div className="flex items-center gap-3 p-4 rounded-lg mb-4" style={{ backgroundColor: 'var(--soft-color)', borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--text-color)' }}>
              <input
                type="checkbox"
                id="retirada_local"
                checked={formData.retirada_local}
                onChange={(e: any) => {
                  const isChecked = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    retirada_local: isChecked,
                    endereco: isChecked ? '' : prev.endereco
                  }));
                  // Clear address error, frete info, and selected address when switching to pickup
                  if (isChecked) {
                    setErrors(prev => ({ ...prev, endereco: undefined }));
                    setFreteInfo({});
                    setSelectedAddress(null);
                  }
                }}
                className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-2"
                style={{ accentColor: 'var(--bg-color)' }}
              />
              <label htmlFor="retirada_local" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--badge-color)' }}>
                Retirada no local (sem entrega)
              </label>
            </div>

            {!formData.retirada_local && (
              <>
                <div>
                  <label className="label text-[15px]">Endereço de Entrega *</label>
                  <button
                    type="button"
                    onClick={() => setIsAddressModalOpen(true)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center justify-between ${
                      errors.endereco
                        ? 'border-error-300 bg-error-50'
                        : selectedAddress
                        ? 'border-gray-300 bg-white hover:border-gray-400'
                        : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    {selectedAddress ? (
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--text-color)' }} />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {selectedAddress.rua}, {selectedAddress.numero}
                            {selectedAddress.apelido && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'var(--soft-color)', color: 'var(--text-color)' }}>
                                {selectedAddress.apelido}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {selectedAddress.bairro}, {selectedAddress.cidade} - {selectedAddress.estado}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Selecione um endereço de entrega</span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                  </button>
                  {errors.endereco && (
                    <p className="mt-1 text-xs text-error-600">{errors.endereco}</p>
                  )}
                </div>

                <AddressSelectionModal
                  isOpen={isAddressModalOpen}
                  onClose={() => setIsAddressModalOpen(false)}
                  onSelect={handleAddressSelect}
                  linkId={linkId}
                />

                {/* Exibicao do Frete */}
                {calculatingFrete && (
                  <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                      <span className="text-gray-600">Calculando frete...</span>
                    </div>
                  </div>
                )}

                {!calculatingFrete && freteInfo.valor_frete !== undefined && freteInfo.can_deliver && (
                  <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--soft-color)' }}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Truck className="w-5 h-5" style={{ color: 'var(--text-color)' }} />
                        <span className="font-medium" style={{ color: 'var(--text-color)' }}>
                          Frete
                        </span>
                      </div>
                      <span className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>
                        R$ {freteInfo.valor_frete.toFixed(2).replace('.', ',')}
                      </span>
                    </div>
                    {freteInfo.distancia_km && (
                      <p className="text-sm text-gray-600 mt-1">
                        Distância: {freteInfo.distancia_km.toFixed(1)} km
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {formData.retirada_local && (
              <div className="p-4 bg-success-50 border border-success-200 rounded-lg">
                <div className="text-sm text-success-800 space-y-2">
                  <p>Você irá retirar o pedido no local</p>
                  <p>Não haverá cobrança de frete</p>
                </div>
              </div>
            )}
          </div>

          {/* Pagamento */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Forma de Pagamento
              </h2>
            </div>

            <div className="space-y-4">
              <div>
                <Select
                  label="Meio de Pagamento *"
                  value={formData.meio_pagamento}
                  onChange={(value: any) => handleChange('meio_pagamento', value)}
                  options={paymentMethods}
                  placeholder="Selecione..."
                  error={errors.meio_pagamento}
                  className={errors.meio_pagamento ? 'border-error-500' : ''}
                />
              </div>

              {showTrocoField && (
                <div>
                  <Input
                    label="Troco para quanto?"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.troco}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      // Apenas aceita valores positivos ou vazio
                      if (value === '' || parseFloat(value) >= 0) {
                        handleChange('troco', value);
                      }
                    }}
                    placeholder="Ex: 50.00"
                    error={errors.troco}
                    helperText={`Total do pedido: R$ ${total.toFixed(2).replace('.', ',')}${valorFrete > 0 ? ' (inclui frete)' : ''}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Observações */}
          <div className="bg-white p-[20px] rounded-[4px] border-[1.5px] border-gray-900">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5" style={{ stroke: 'var(--text-color)' }} />
              <h2 className="text-[22px] font-extrabold tracking-tight text-gray-900">
                Observações
              </h2>
            </div>

            <div>
              <label className="label text-[15px]">Observações (opcional)</label>
              <textarea
                value={formData.observacoes}
                onChange={(e: any) => handleChange('observacoes', e.target.value)}
                placeholder="Alguma observação sobre seu pedido?"
                rows={3}
                className="input resize-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn btn-order-link text-[15px] py-4"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                Confirmar Pedido
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CheckoutForm;
